import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock, FileText } from 'lucide-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import PortalBackground from '../components/PortalBackground';

/**
 * DocuSign redirects here after embedded bid-contract signing.
 * The webhook does the authoritative completion work (signed PDF → documents +
 * bid card); this page records the client-side signature optimistically and
 * routes back to Billing where the quote card lives.
 */
const BidContractSigningReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');

  const billingPath = '/portal/billing';

  useEffect(() => {
    const event = searchParams.get('event');
    const envelopeId = searchParams.get('envelopeId');

    const handleReturn = async () => {
      if (!currentUser) {
        setStatus('error');
        setMessage('You must be logged in to complete this step.');
        return;
      }

      if (event === 'signing_complete') {
        if (envelopeId) {
          try {
            const signingRef = doc(db, 'bid_contract_signings', envelopeId);
            const signingSnap = await getDoc(signingRef);
            if (signingSnap.exists()) {
              const signingData = signingSnap.data();
              await updateDoc(signingRef, {
                signingComplete: true,
                clientSignedAt: new Date().toISOString(),
              });

              if (signingData.bidId) {
                const bidRef = doc(db, 'alaCarteBids', signingData.bidId);
                const bidSnap = await getDoc(bidRef);
                if (bidSnap.exists()) {
                  const contract = bidSnap.data().contract || {};
                  // The webhook owns 'completed' — only advance sent → client_signed.
                  if (contract.envelopeId === envelopeId && contract.status !== 'completed') {
                    await updateDoc(bidRef, {
                      contract: { ...contract, status: 'client_signed' },
                      updatedAt: new Date(),
                    });
                  }
                }
              }
            }
          } catch (err) {
            console.warn('Could not record bid contract signature client-side:', err);
          }
        }

        setStatus('complete');
        setMessage('Your contract has been signed. The BBA team will countersign and get started.');
        setTimeout(() => navigate(billingPath), 2500);
      } else if (event === 'decline') {
        setStatus('declined');
        setMessage('You declined to sign the contract. You can return and sign whenever you are ready.');
      } else if (event === 'cancel') {
        setStatus('declined');
        setMessage('You cancelled the signing session. You can pick it back up from your quotes at any time.');
      } else if (event === 'session_timeout' || event === 'ttl_expired') {
        setStatus('error');
        setMessage('Your signing session expired. Head back to your quotes and try again.');
      } else {
        navigate(billingPath);
      }
    };

    handleReturn();
  }, [searchParams, currentUser, navigate]);

  return (
    <div className="relative min-h-screen flex items-center justify-center">
      <PortalBackground />
      <div className="relative max-w-md w-full mx-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="flex justify-center mb-4">
                <Clock className="h-14 w-14 text-blue-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Processing your signature…
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Please wait a moment.</p>
            </>
          )}

          {status === 'complete' && (
            <>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-14 w-14 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Contract Signed!
              </h2>
              <p className="text-sm mb-6 text-gray-600 dark:text-gray-400">{message}</p>
            </>
          )}

          {status === 'declined' && (
            <>
              <div className="flex justify-center mb-4">
                <FileText className="h-14 w-14 text-yellow-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Signing Not Completed
              </h2>
              <p className="text-sm mb-6 text-gray-600 dark:text-gray-400">{message}</p>
              <button
                type="button"
                onClick={() => navigate(billingPath)}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Back to My Quotes
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <AlertCircle className="h-14 w-14 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Something Went Wrong
              </h2>
              <p className="text-sm mb-6 text-gray-600 dark:text-gray-400">{message}</p>
              <button
                type="button"
                onClick={() => navigate(billingPath)}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Back to My Quotes
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BidContractSigningReturn;
